export interface ParsedCsvFile {
  columns: string[]
  rows: Record<string, string>[]
  hasDuplicateHeaders: boolean
}

/**
 * Cleans a CSV header cell (BOM, quotes, whitespace, zero-width chars).
 */
export function cleanCsvColumnName(name: string): string {
  if (!name) return ''

  return name
    .trim()
    .replace(/\uFEFF/g, '')
    .replace(/\u00A0/g, '')
    .replace(/[\r\n\t]/g, '')
    .replace(/[\u200B-\u200D\u2060]/g, '')
    .replace(/^["']|["']$/g, '')
}

/**
 * Parses a single CSV line respecting quoted fields and escaped quotes.
 * @param line {string} - The CSV line.
 * @returns The parsed CSV line.
 */
export function splitCsvLine(line: string): string[] {
  const values: string[] = []
  let current = ''
  let inQuotes = false

  for (let index = 0; index < line.length; index++) {
    const char = line[index]
    const nextChar = line[index + 1]

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"'
        index++
      } else {
        inQuotes = !inQuotes
      }
      continue
    }

    if (char === ',' && !inQuotes) {
      values.push(current.trim())
      current = ''
      continue
    }

    current += char
  }

  values.push(current.trim())
  return values
}

/**
 * Parses CSV text into headers and row objects.
 * @param csvText {string} - The CSV text.
 * @returns The parsed CSV file.
 */
export function parseCsvText(csvText: string): ParsedCsvFile {
  const unifiedLineBreaksText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = unifiedLineBreaksText.split('\n').filter((line) => line.trim())

  if (lines.length === 0) {
    return { columns: [], rows: [], hasDuplicateHeaders: false }
  }

  const columns = splitCsvLine(lines[0]).map((header) => cleanCsvColumnName(header))
  const uniqueHeaders = new Set(columns)
  const hasDuplicateHeaders = uniqueHeaders.size !== columns.length

  if (hasDuplicateHeaders) {
    return { columns, rows: [], hasDuplicateHeaders: true }
  }

  const rows = lines.slice(1).map((line) => {
    const values = splitCsvLine(line)
    const row: Record<string, string> = {}

    columns.forEach((column, columnIndex) => {
      row[column] = values[columnIndex] ?? ''
    })

    return row
  })

  return {
    columns,
    rows,
    hasDuplicateHeaders: false
  }
}
